/**
 * Cameras — admin live-stream gallery page.
 *
 * Lists every active TripCameraStream (Status = 1 = Recording) and lets the
 * operator open any of them in the reusable <CameraPlayer> component.
 *
 * Data sources:
 *   GET /api/admin/CameraDevices/Streams?status=1&page=N
 *     → { Total, Items: [ { Id, TripsId, StreamId, StartedAt, EndedAt,
 *                            Status, RetentionExpiresAt, LegalHold,
 *                            RecordingSizeBytes } ] }
 *
 *   GET /api/admin/Tracking/ActiveTrips  (best-effort enrich — driver/passenger/vehicle)
 *     → [ { tripId, driver, passenger, vehicle, type, ... } ]
 *
 *   GET /api/Cameras/Trip/{tripId}/Live  (CameraPlayer handles this itself)
 *
 * Behaviour:
 *   - Auto-refreshes the stream list every 10 s (configurable via `refreshIntervalMs`).
 *   - Pauses refresh when the tab is hidden (visibilitychange) to save bandwidth.
 *   - Selecting a card opens the player; the player handles its own retry logic.
 */
import moment from 'moment';
import axios from 'axios';
import HelperMixin from '../../Shared/HelperMixin.vue';
import CameraPlayer from '../CameraPlayer/CameraPlayer.vue';

const REFRESH_INTERVAL_MS = 10000;

export default {
    name: 'Cameras',
    mixins: [HelperMixin],

    components: { CameraPlayer },

    data() {
        return {
            streams: [],          // { id, tripId, streamId, startedAt, driver, passenger, vehicle, hasMeta }
            tripsIndex: {},       // tripId -> { driver, passenger, vehicle, type, status }
            selectedStreamId: null,
            loading: true,
            lastRefreshAt: null,
            refreshIntervalMs: REFRESH_INTERVAL_MS,
            refreshTimer: null,
            paused: false,        // tab-hidden pause
            errorBanner: '',
            searchQuery: '',
            playerState: 'idle',
        };
    },

    computed: {
        filteredStreams() {
            const q = (this.searchQuery || '').trim().toLowerCase();
            if (!q) return this.streams;
            return this.streams.filter(s =>
                (s.driver    && s.driver.toLowerCase().includes(q))    ||
                (s.passenger && s.passenger.toLowerCase().includes(q)) ||
                (s.vehicle   && String(s.vehicle).toLowerCase().includes(q)) ||
                (s.tripId    && String(s.tripId).includes(q))          ||
                (s.streamId  && String(s.streamId).toLowerCase().includes(q))
            );
        },

        selectedStream() {
            return this.streams.find(s => s.id === this.selectedStreamId) || null;
        },

        refreshLabel() {
            if (this.paused)       return 'متوقّف مؤقتاً';
            if (!this.lastRefreshAt) return 'في انتظار التحديث الأول…';
            return `تحديث تلقائي كل ${Math.round(this.refreshIntervalMs / 1000)} ث`;
        },

        lastRefreshText() {
            if (!this.lastRefreshAt) return '';
            try { return moment(this.lastRefreshAt).format('HH:mm:ss'); }
            catch (_) { return ''; }
        },
    },

    async created() {
        this.loginDetails = await this.CheckLoginStatus();
        if (!this.requireUserType([1])) return;
        // First fetch
        await this.refresh();
        this.startAutoRefresh();
        // Pause auto-refresh when the tab is backgrounded — saves bandwidth and
        // keeps the per-camera 30-second JWTs from churning while no one watches.
        if (typeof document !== 'undefined') {
            this._visibilityHandler = () => {
                if (document.hidden) {
                    this.stopAutoRefresh();
                    this.paused = true;
                } else {
                    this.paused = false;
                    this.refresh();
                    this.startAutoRefresh();
                }
            };
            document.addEventListener('visibilitychange', this._visibilityHandler);
        }
    },

    beforeDestroy() {
        this.stopAutoRefresh();
        if (this._visibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._visibilityHandler);
            this._visibilityHandler = null;
        }
    },

    methods: {

        // ============ Refresh loop ============

        startAutoRefresh() {
            this.stopAutoRefresh();
            this.refreshTimer = setInterval(() => {
                this.refresh();
            }, this.refreshIntervalMs);
        },

        stopAutoRefresh() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
                this.refreshTimer = null;
            }
        },

        async refresh() {
            try {
                this.errorBanner = '';
                // 1. Get every active stream (Status=1)
                const res = await axios.get('/api/admin/CameraDevices/Streams', {
                    params: { status: 1, page: 1 },
                    withCredentials: true,
                });
                const items = (res && res.data && res.data.Items) || [];

                // 2. Try to enrich with trip data — best effort, never blocks the list.
                await this.refreshTripIndex();

                // 3. Normalise + merge.
                this.streams = items.map(it => {
                    const tripId = it.TripsId ?? it.tripsId ?? it.TripId ?? null;
                    const meta = tripId != null ? (this.tripsIndex[tripId] || {}) : {};
                    return {
                        id:           it.Id ?? it.id,
                        tripId:       tripId,
                        streamId:     it.StreamId ?? it.streamId ?? '',
                        startedAt:    it.StartedAt ?? it.startedAt ?? null,
                        endedAt:      it.EndedAt ?? it.endedAt ?? null,
                        status:       it.Status ?? it.status ?? 1,
                        legalHold:    !!(it.LegalHold ?? it.legalHold),
                        driver:       meta.driver    || '',
                        passenger:    meta.passenger || '',
                        vehicle:      meta.vehicle   || '',
                        tripType:     meta.type      || null,
                        tripStatus:   meta.status    || '',
                        hasMeta:      Object.keys(meta).length > 0,
                    };
                });

                this.lastRefreshAt = Date.now();

                // If the previously-selected stream disappeared (trip ended),
                // clear the player so we don't sit on a dead m3u8.
                if (this.selectedStreamId && !this.streams.some(s => s.id === this.selectedStreamId)) {
                    this.selectedStreamId = null;
                }
            } catch (err) {
                const code = err && err.response && err.response.status;
                if (code === 401) {
                    this.logout();
                    return;
                }
                this.errorBanner = 'تعذّر تحميل قائمة البث المباشر — سيُعاد المحاولة تلقائياً.';
                // eslint-disable-next-line no-console
                console.warn('[Cameras] refresh failed:', err && err.message);
            } finally {
                this.loading = false;
            }
        },

        async refreshTripIndex() {
            // Best-effort. If LiveTracking hub isn't backing this admin, we
            // simply show TripId only — the player still works because it
            // keys off TripId directly.
            try {
                const res = await axios.get('/api/admin/Tracking/ActiveTrips', { withCredentials: true });
                const payload = res && res.data;
                let trips = [];
                if (Array.isArray(payload)) trips = payload;
                else if (payload && Array.isArray(payload.Trips)) trips = payload.Trips;
                else if (payload && Array.isArray(payload.trips)) trips = payload.trips;

                const idx = {};
                trips.forEach(t => {
                    const id = t.tripId ?? t.TripId ?? t.id;
                    if (id == null) return;
                    idx[id] = {
                        driver:    t.driver    ?? t.Driver    ?? t.driverName    ?? '',
                        passenger: t.passenger ?? t.Passenger ?? t.passengerName ?? '',
                        vehicle:   t.vehicle   ?? t.Vehicle   ?? t.plate         ?? t.VehiclePlate ?? '',
                        type:      t.type      ?? t.Type      ?? null,
                        status:    t.status    ?? t.Status    ?? '',
                    };
                });
                this.tripsIndex = idx;
            } catch (_) {
                // Silent — enrichment is optional. Keep the previous index so we
                // don't lose information just because the hub flapped.
            }
        },

        // ============ Selection ============

        selectStream(stream) {
            if (!stream) return;
            this.selectedStreamId = stream.id;
        },

        closePlayer() {
            this.selectedStreamId = null;
        },

        onPlayerState(payload) {
            this.playerState = (payload && payload.state) || 'idle';
        },

        // ============ Helpers ============

        formatTime(iso) {
            if (!iso) return '—';
            try { return moment(iso).format('HH:mm:ss'); }
            catch (_) { return iso; }
        },

        formatRelative(iso) {
            if (!iso) return '—';
            const t = new Date(iso).getTime();
            if (Number.isNaN(t)) return '—';
            const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
            if (sec < 60)   return `قبل ${sec} ث`;
            if (sec < 3600) return `قبل ${Math.round(sec / 60)} د`;
            return `قبل ${Math.round(sec / 3600)} س`;
        },

        tripTypeLabel(t) {
            switch (Number(t)) {
                case 1: return 'عادية';
                case 2: return 'مشتركة';
                case 3: return 'مدرسية';
                default: return '—';
            }
        },
    }
};
